<script setup>
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useKanbanStore, KANBAN_STATUSES } from 'dashboard/stores/kanban';

import KanbanCard from './KanbanCard.vue';

const { t } = useI18n();
const kanbanStore = useKanbanStore();

const columnTitle = status => t(`KANBAN.COLUMNS.${status.toUpperCase()}`);

// Fires once per drop, on the column the card was dropped into. The card
// has already been spliced into `kanbanStore.columns[toStatus]` by
// vuedraggable at this point (it mutates the bound :list in place), so this
// only needs to confirm the change with the API and resync on failure.
const onChange = async (toStatus, event) => {
  const added = event?.added;
  if (!added) return;

  try {
    await kanbanStore.confirmStatusChange({
      conversationId: added.element.id,
      toStatus,
    });
  } catch (error) {
    useAlert(t('KANBAN.MOVE_ERROR'));
    // The optimistic move done by vuedraggable is now out of sync with the
    // server — simplest correct fix is to refetch every column.
    kanbanStore.fetchAll();
  }
};
</script>

<template>
  <div class="flex flex-1 gap-4 p-4 overflow-x-auto">
    <div
      v-for="status in KANBAN_STATUSES"
      :key="status"
      class="flex flex-col flex-shrink-0 w-72 bg-n-solid-2 border border-n-weak rounded-xl"
    >
      <div
        class="flex items-center justify-between px-3 py-2.5 border-b border-n-weak"
      >
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ columnTitle(status) }}
        </h3>
        <span class="text-xs text-n-slate-10">
          {{ kanbanStore.getColumn(status).length }}
        </span>
      </div>

      <draggable
        :list="kanbanStore.columns[status]"
        item-key="id"
        group="kanban-conversations"
        class="flex flex-col flex-1 gap-2 p-2 overflow-y-auto min-h-[120px]"
        ghost-class="opacity-40"
        @change="event => onChange(status, event)"
      >
        <template #item="{ element }">
          <KanbanCard
            :conversation="element"
            :is-moving="kanbanStore.uiFlags.movingConversationId === element.id"
          />
        </template>
        <template #footer>
          <p
            v-if="!kanbanStore.uiFlags.fetchingList && !kanbanStore.getColumn(status).length"
            class="py-4 text-xs text-center text-n-slate-9"
          >
            {{ t('KANBAN.EMPTY_COLUMN') }}
          </p>
        </template>
      </draggable>
    </div>
  </div>
</template>
